'use client';

import { memo, Fragment, useMemo } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { PathInfo, FolderType } from './useAssetPicker';
import { FOLDER_TYPE_TRANSLATION_KEY } from './folder-type-constants';

export interface AssetPickerBreadcrumbProps {
  path: PathInfo[];
  onNavigate: (index: number) => void;
}

/**
 * 에셋 선택 모달용 브레드크럼 컴포넌트
 * - 현재 폴더 경로 표시
 * - 클릭 시 해당 경로로 이동
 */
export const AssetPickerBreadcrumb = memo(function AssetPickerBreadcrumb({
  path,
  onNavigate,
}: AssetPickerBreadcrumbProps) {
  const t = useTranslations();

  // 시스템 폴더의 경우 번역된 이름 반환
  const getDisplayName = useMemo(() => {
    return (folder: PathInfo) => {
      if (
        folder.folderType !== undefined &&
        folder.folderType !== FolderType.CUSTOM
      ) {
        const translationKey = FOLDER_TYPE_TRANSLATION_KEY[folder.folderType];
        if (translationKey) {
          return t(translationKey);
        }
      }
      return folder.name;
    };
  }, [t]);

  if (path.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {path.map((folder, index) => {
          const isLast = index === path.length - 1;
          const displayName = getDisplayName(folder);

          return (
            <Fragment key={folder.id}>
              {index > 0 && <BreadcrumbSeparator />}
              {isLast ? (
                <BreadcrumbPage>{displayName}</BreadcrumbPage>
              ) : (
                <BreadcrumbItem>
                  <button
                    type="button"
                    onClick={() => onNavigate(index)}
                    className="cursor-pointer text-sys-cont-neutral-light hover:text-sys-cont-neutral-default hover:underline"
                  >
                    {displayName}
                  </button>
                </BreadcrumbItem>
              )}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
});

export default AssetPickerBreadcrumb;
